import { useState } from 'react'
import { BedDouble, CalendarDays, Clock, Scale, UserMinus, Users } from 'lucide-react'
import { EndpointHint, HelpIcon, LoadFailure, Skeleton } from '../../core/index.js'
import { useStaffingReport } from '../hooks.js'
import type { StaffingDayKindSummary, StaffingUnitMonth } from '../types.js'
import { describePeriod } from './preferencesText.js'
import { shortDay } from './censusCardData.js'

const SPANS = [1, 3, 6] as const
type Span = (typeof SPANS)[number]

const hppd = (value: number | null): string => (value === null ? '—' : value.toFixed(2))

function Stat({ icon, value, label, sub, tone = 'plain' }: { icon: React.ReactNode; value: string; label: string; sub?: string; tone?: 'good' | 'warn' | 'plain' }) {
  const toneClass = tone === 'good' ? 'text-green' : tone === 'warn' ? 'text-coral' : 'text-ink'
  return (
    <div className="flex items-start gap-3 rounded-lg border border-marine/15 bg-white px-3 py-2.5">
      <span className={`mt-0.5 shrink-0 ${toneClass}`}>{icon}</span>
      <div className="min-w-0">
        <div className={`text-xl font-black leading-tight ${toneClass}`}>{value}</div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
        {sub && <div className="text-[10px] text-ink-mid">{sub}</div>}
      </div>
    </div>
  )
}

/** How the worked figure stands against the target, for the colour. */
function toneOf(worked: number | null, target: number | null): 'good' | 'warn' | 'plain' {
  if (worked === null || target === null) {
    return 'plain'
  }
  return worked >= target - 0.05 ? 'good' : 'warn'
}

/** "Weekend 3.12 · weekday 3.51 HPPD worked", where the API says (F6). */
function weekendLine(weekend: StaffingDayKindSummary | undefined, weekday: StaffingDayKindSummary | undefined): string | null {
  if (!weekend || !weekday || (weekend.days === 0 && weekday.days === 0)) {
    return null
  }
  return `Weekend ${hppd(weekend.workedHppd)} · weekday ${hppd(weekday.workedHppd)} HPPD worked`
}

function UnitMonth({ unit }: { unit: StaffingUnitMonth }) {
  const [open, setOpen] = useState(false)
  const known = unit.days.filter((day) => day.census !== null)
  return (
    <li className="py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs font-bold text-ink">
          {unit.unitName} <span className="font-normal text-ink-mid">· {describePeriod(unit.period)}</span>
        </div>
        <div className="text-[11px] text-ink-mid">
          PPD {unit.ppd} over {unit.daysWithCensus} of {unit.daysInMonth} days · scheduled {hppd(unit.scheduledHppd)} · worked <span className={toneOf(unit.workedHppd, unit.targetHppd) === 'warn' ? 'text-coral font-bold' : toneOf(unit.workedHppd, unit.targetHppd) === 'good' ? 'text-green font-bold' : ''}>{hppd(unit.workedHppd)}</span>
          {unit.targetHppd !== null && ` against ${hppd(unit.targetHppd)} HPPD`}
        </div>
      </div>
      {unit.byRole.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-3 text-[10px] text-ink-mid">
          {unit.byRole.map((role) => (
            <li key={role.role}>
              <span className="font-bold text-ink">{role.role}</span> {hppd(role.workedHppd)} worked{role.targetHppd !== null ? ` / ${hppd(role.targetHppd)} target` : ''} · {role.workedHours} h
            </li>
          ))}
        </ul>
      )}
      {weekendLine(unit.weekend, unit.weekday) && (
        <div className={`mt-1 text-[10px] ${toneOf(unit.weekend?.workedHppd ?? null, unit.targetHppd) === 'warn' ? 'text-coral' : 'text-ink-mid'}`}>{weekendLine(unit.weekend, unit.weekday)}</div>
      )}
      <div className="mt-1 text-[10px] text-ink-mid">
        {unit.scheduledHours} h scheduled · {unit.workedHours} h worked{unit.agencyHours > 0 && ` (${unit.agencyHours} h agency)`}{unit.flexedHours > 0 && ` · ${unit.flexedHours} h released for low census`}
        {known.length > 0 && (
          <button type="button" className="ml-2 font-bold text-marine" onClick={() => setOpen((current) => !current)}>
            {open ? 'Hide the days' : 'Show the days'}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-1 overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-left font-bold uppercase tracking-wide text-marine">
                <th className="py-0.5 pr-2">Day</th>
                <th className="px-2 text-right">PPD</th>
                <th className="px-2 text-right">Scheduled h</th>
                <th className="px-2 text-right">Worked h</th>
                <th className="px-2 text-right">Agency h</th>
                <th className="px-2 text-right">Released h</th>
                <th className="px-2 text-right">Scheduled HPPD</th>
                <th className="px-2 text-right">Worked HPPD</th>
                <th className="px-2">By role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-marine/10">
              {known.map((day) => (
                <tr key={day.date} className={day.weekend ? 'bg-marine/5' : ''}>
                  <td className="py-0.5 pr-2 whitespace-nowrap text-ink-mid">{shortDay(day.date)}</td>
                  <td className="px-2 text-right font-bold text-ink">{day.census}</td>
                  <td className="px-2 text-right">{day.scheduledHours}</td>
                  <td className="px-2 text-right">{day.workedHours}</td>
                  <td className="px-2 text-right">{day.agencyHours || ''}</td>
                  <td className="px-2 text-right">{day.flexedHours || ''}</td>
                  <td className="px-2 text-right">{hppd(day.scheduledHppd)}</td>
                  <td className={`px-2 text-right ${toneOf(day.workedHppd, unit.targetHppd) === 'warn' ? 'text-coral' : ''}`}>{hppd(day.workedHppd)}</td>
                  <td className="px-2 whitespace-nowrap text-ink-mid">{(day.byRole ?? []).map((role) => `${role.role} ${hppd(role.workedHppd)}`).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </li>
  )
}

/**
 * The staffing report on the Reporting tab (census response plan §3.6): the
 * span's PPD and HPPD, scheduled against worked, as the stat tiles, then each
 * unit's month with the hours by role and the days on request. The check a
 * DON does after the fact, in the terms PBJ and Care Compare use.
 */
export function StaffingReportCard({ facilityId }: { facilityId: string }) {
  const [span, setSpan] = useState<Span>(1)
  const query = useStaffingReport(facilityId, span)
  const report = query.data
  return (
    <section className="rounded border border-marine/15 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1 text-sm font-black text-ink">
            Staffing
            <HelpIcon topic="schedule.staffing-report" />
            <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/reports/staffing?months=</EndpointHint>
          </div>
          <p className="max-w-xl text-[11px] leading-relaxed text-ink-mid">Hours per patient day, scheduled and worked, against your target - by unit and day, from the census you recorded, the released month and the shifts reported. PPD is patient days: a day’s census, summed over the days that have one. A day with no recorded census shows its hours and no HPPD. Weekends are shown apart, as Care Compare reports them.</p>
        </div>
        <div role="radiogroup" aria-label="Months" className="flex gap-1">
          {SPANS.map((choice) => (
            <button key={choice} type="button" role="radio" aria-checked={span === choice} onClick={() => setSpan(choice)} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${span === choice ? 'border-marine bg-marine text-white' : 'border-marine/30 text-ink-mid hover:border-marine'}`}>
              {choice === 1 ? 'This month' : `${choice} months`}
            </button>
          ))}
        </div>
      </div>
      {query.isPending ? (
        <Skeleton className="mt-3" />
      ) : query.isError || !report ? (
        <LoadFailure size="xs" className="mt-3" message="The staffing report could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
            <Stat icon={<BedDouble className="h-4 w-4" />} value={String(report.totals.ppd)} label="PPD" sub="patient days with a recorded census" />
            <Stat icon={<Clock className="h-4 w-4" />} value={hppd(report.totals.scheduledHppd)} label="Scheduled HPPD" sub={`${report.totals.scheduledHours} h scheduled`} />
            <Stat icon={<Scale className="h-4 w-4" />} value={hppd(report.totals.workedHppd)} label="Worked HPPD" sub={report.targetHppd !== null ? `target ${hppd(report.targetHppd)}` : 'no HPPD target set'} tone={toneOf(report.totals.workedHppd, report.targetHppd)} />
            <Stat icon={<Users className="h-4 w-4" />} value={String(report.totals.agencyHours)} label="Agency hours" sub="of the hours worked" />
            <Stat icon={<UserMinus className="h-4 w-4" />} value={String(report.totals.flexedHours)} label="Released hours" sub="low census, seats held" />
            {report.totals.weekend && <Stat icon={<CalendarDays className="h-4 w-4" />} value={hppd(report.totals.weekend.workedHppd)} label="Weekend HPPD" sub={`weekday ${hppd(report.totals.weekday?.workedHppd ?? null)}, worked`} tone={toneOf(report.totals.weekend.workedHppd, report.targetHppd)} />}
          </div>
          {report.units.length === 0 ? <p className="mt-3 text-[11px] text-ink-mid">No units to report on. List the facility’s units under Configuration › Details.</p> : (
            <ul className="mt-3 divide-y divide-marine/10">
              {report.units.map((unit) => (
                <UnitMonth key={`${unit.period}-${unit.unitId}`} unit={unit} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
